import axios, { InternalAxiosRequestConfig, AxiosRequestConfig } from 'axios';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
// config
import { HOST_API_LEGACY, HOST_API, FIREBASE_API } from 'src/config-global';
import { paths } from 'src/routes/paths';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { initializeApp } from 'firebase/app';
// utils
// import SnackbarUtils from 'src/utils/snackbar-utils-configurator';
import {
  localStorageGetItem,
  localStorageSetItem,
  localStorageRemoveItem,
  AUTH_STORAGE_KEYS,
} from './storage-available';

// import { toSnakeCaseKeys } from './misc';

// ----------------------------------------------------------------------

export const endpoints = {
  auth: {
    login: '/auth/login',
    refreshToken: '/auth/refresh-token',
    passwords: '/auth/passwords',
    firebase: {
      loginWithPassword: '/auth/login/new',
    },
  },
  admin: {
    tableText: '/admin/texts',
    tableTextByKey: (key: string) => `/admin/text/${key}`,
    producer_dues: '/admin/dashboard/producer-dues',
    clients: '/admin/clients',
    companies: '/admin/companies',
    clientsExcel: '/admin/clients-excel',
    agentsStat: '/admin/agents_stat',
    agents: '/admin/agents',
    agentServiceZone: '/admin/agents/agent-service-area',
    clientRegions: '/admin/client_regions',
    clientRegionsProvinces: '/admin/client_all_region_provinces',
    countries: '/admin/countries',
    agent_associated_clients: '/admin/agent/associated-clients',
    client_discount: 'stats/discount',
    overlay: {
      root: '/admin/overlay',
      activePDF: '/admin/overlay/active/pdf',
    },
    targets: '/admin/targets',
    logisticaIt: {
      syncProducers: '/admin/logistica-it/sync-producers',
      syncProducts: '/admin/logistica-it/sync-products',
      syncClients: '/admin/logistica-it/sync-clients',
      syncStock: '/admin/logistica-it/sync-stock',
    },
    ficSync: {
      syncVat: '/admin/clients/sync-fic-vat',
    },
    fic_suppliers: '/admin/fic-suppliers',
    catalogPdf: {
      generate: '/admin/catalog/pdf/generate',
      status: (jobId: string) => `/admin/catalog/pdf/status/${jobId}`,
      jobs: '/admin/catalog/pdf/jobs',
    },
    overview: {
      logistics: '/admin/dashboard/logistica-orders',
      administration: '/admin/dashboard/administration',
      currentMonthStats: '/admin/dashboard/current-month-statistics',
      currentQuarterStats: '/admin/dashboard/current-quarter-statistics',
      logisticsCsv: '/admin/logistics/courier/csv/download',
      dachserFercamCsv: '/admin/logistics/dachser-fercam/csv/download',
      logisticsCsvEmail: '/admin/logistics/couriers/reports/delivery-email-csv',
      exciseReportEmail: '/admin/excise-report/email',
      target: '/admin/dashboard/target',
      optimizationTarget: '/admin/dashboard/optimization-target',
      yearlyTrends: '/admin/dashboard/financial-yearly-trends',
      financials: '/admin/dashboard/financials',
      incomingOutgoingCashflow: '/admin/dashboard/incoming-outgoing-cashflow',
      salesPerformance: '/admin/dashboard/sales-performance',
      weeklySalesPerformance: '/admin/dashboard/weekly-sales-performance',
      slowMovingWines: '/admin/dashboard/slow-moving-wines',
      getKpiSummary: '/admin/dashboard/daily-kpi-summary',
      agingReceivables: '/admin/dashboard/aging/receivables',
      agingPayables: '/admin/dashboard/aging/payables',
    },
    orders: {
      client_orders: '/admin/client_orders',
      notifyStorageAboutClientOrder: (order_code: string) =>
        `/admin/client_orders/${order_code}/notify_storage`,
      notifyLogisticaAboutClientOrder: (order_code: string) =>
        `/admin/logistica-it/notify-client-order/${order_code}`,
      proFormaClientOrder: (order_code: string) => `/admin/client_orders/${order_code}/proforma`,
      producer_orders: '/admin/producer_orders',
      updateClientOrderProduct: (order_code: string, id: string) =>
        `/admin/client_orders/${order_code}/client_order_products/${id}`,
      createClientOrderProduct: (order_code: string) =>
        `/admin/client_orders/${order_code}/client_order_products`,
      notifyStorageAboutProducerOrder: (order_code: string) =>
        `/admin/producer_orders/${order_code}/notify_storage`,
      notifyLogisticaAboutProducerOrder: (order_code: string) =>
        `/admin/logistica-it/notify-supplier-order/${order_code}`,
      addAvailability: (order_code: string) =>
        `/admin/producer_orders/${order_code}/add-availability`,
      producer_order_payments: `/admin/producer_order_payments`,
      payment_link: '/admin/payment_link',
      client_order_discount: '/admin/client_order_discount',
    },
    reconciliation: {
      queue: '/admin/reconciliation/queue',
      ops: '/admin/reconciliation/ops',
      wire: (id: string) => `/admin/reconciliation/wires/${id}`,
      reverse: (id: string) => `/admin/reconciliation/wires/${id}/reverse`,
      dismissals: '/admin/reconciliation/dismissals',
      openPool: '/admin/reconciliation/open-pool',
      apply: (id: string) => `/admin/reconciliation/wires/${id}/apply`,
      attribute: (id: string) => `/admin/reconciliation/wires/${id}/attribute`,
      partialInvoices: '/admin/reconciliation/partial-invoices',
      customerBalances: '/admin/reconciliation/customer-balances',
      activity: '/admin/reconciliation/activity',
      pending: '/admin/reconciliation/pending',
    },
    orderPdf: '/admin/pdf/order',
    courtesyInvoicePdf: '/admin/pdf/courtesy-invoice',
    courier: '/admin/courier',
    products: {
      root: '/admin/products',
      checkSku: '/admin/products/check-sku',
      allProducts: '/admin/products',
      reorderProducts: '/admin/reorder',
      updateReorderProducts: '/admin/products/reorder-status',
      pdf: '/admin/products/pdf',
      reservation: (sku: string) => `/admin/products/${sku}/reservations`,
      reservations: (sku: string, client_slug: string) =>
        `/admin/products/${sku}/reservations/${client_slug}`,
      grapeTypes: '/admin/products/grape_types',
      producerCountries: '/admin/products/filter-options/producer-countries',
      discount: (sku: string) => `/admin/products/${sku}/discounts`,
      deleteDiscounts: (id: string, product_id: string) =>
        `/admin/products/${product_id}/discounts/${id}`,
    },
    wineTypes: '/admin/wine_types',
    wineListing: '/admin/wine_listing_data',
    wineCategories: '/admin/wine_categories',
    grapeTypes: '/admin/grape_types',
    territories: '/admin/territories',
    producers: '/admin/producers',
    producer: '/admin/producer',
    producerRegions: '/admin/producer_regions',
    producerRegionsProvinces: '/admin/producer_all_region_provinces',
    productSelections: {
      root: '/admin/product-selections',
      byId: (id: string | number) => `/admin/product-selections/${id}`,
      items: (id: string | number) => `/admin/product-selections/${id}/items`,
    },
    users: '/admin/users',
    userOrder: `/admin/users/orders`,
    producer_payments: '/admin/producer_payments',
    deleteProducerPayment: (paymentId: string) => `/admin/producer_payments/${paymentId}`,
    statistics: {
      root: '/admin/stats',
      annualStats: {
        yoy: '/admin/stats/yoy',
      },
      getOrderPlacedByStats: '/admin/stats/order-stats',
      productStats: '/admin/stats/product_stats',
      updateLogisticsCost: '/admin/stats/update_logistics_cost',
      deleteLogisticsCost: (id: string) => `/admin/logistics/details/${id}`,
      logisticsTypeValues: '/admin/stats/get_logistics_type',
      logisticsSupplierList: '/admin/stats/logistics_supplier_list',
      profitability: 'admin/stats/profitability',
      // bottles_flow: 'admin/stats/bottles-flow',
      stock_optimization: 'admin/stats/stock-optimization',
      price_range_stock_behaviour: 'admin/stats/price-range-stock-behaviour',
      single_range_stock_behaviour: 'admin/stats/single-range-stock-behaviour',
      yearly_sales: 'admin/stats/yearly-sales',
      sales_flow: 'admin/stats/sales-flow',
      product_performance: '/admin/product-performance',
      analytics: {
        priceDistribution: '/admin/analytics/price-distribution',
        wineDistribution: '/admin/analytics/wine-distribution',
      },
      producerRisk: '/admin/stats/producer-risk',
    },
    reservations: '/admin/reservations',
    discounts: '/admin/discounts',
    tastings: '/admin/tastings',
    createClient: (slug: string) => `/admin/tastings/${slug}/tasting-clients`,
    deleteClient: (params: { slug: string; clientSlug: string }) =>
      `/admin/tastings/${params.slug}/tasting-clients/${params.clientSlug}`,
    createProduct: (slug: string) => `/admin/tastings/${slug}/tasting-products`,
    deleteProduct: (params: { slug: string; sku: string }) =>
      `/admin/tastings/${params.slug}/tasting-products/${params.sku}`,
  },
  customer: {
    payment_status: '/customer/payment-status',
    selections: '/customer/selections',
    producers: { root: '/customer/producers', countries: '/customer/producers/countries' },
    products: {
      root: '/customer/products',
      volumes: '/customer/products/volumes',
      grape_types: '/customer/products/grape_types',
      wine_types: '/customer/products/wine_types',
      pricing: '/customer/products/pricing',
    },
    client_orders: {
      root: '/customer/client_orders',
      current: '/customer/client_orders/current',
      current_order: '/customer/client_orders/currentOrder',
      details: (order_code: string) => `/customer/client_orders/${order_code}/new`,
    },
    overlay: {
      active: '/customer/overlay/active',
      fallback: '/customer/overlay/fallback',
      mark_seen: '/customer/overlay',
      client_details: '/customer/client-details',
    },
    cart_products: {
      root: '/customer/cart_products',
      checkout: '/customer/checkout',
    },
    clients: '/customer/clients',
  },
  utils: {
    files: {
      link: '/utils/files/link/create',
    },
    clientCategories: '/utils/client-categories',
    userRoles: '/utils/user-roles',
    promotionTypes: '/utils/promotion-types',
  },
  public: {
    clientPdfDownload: (token: string) => `public/products/pdf/${token}/download`,
    generic: `public/products/pdf/download/generic`,
    exciseRegister: (date: string) => `public/excise-register?date=${date}`,
  },
};

export const endpointsLegacy = {
  auth: {
    login: '/auth/login',
    refreshToken: '/auth/refresh-token',
  },
  admin: {
    clients: '/admin/clients',
    agents: '/admin/agents',
    clientRegions: '/admin/client_regions',
    orders: {
      client_orders: '/admin/client_orders',
      producer_orders: '/admin/producer_orders',
    },
    products: {
      root: '/admin/products',
      allProducts: '/admin/products',
      producers: '/admin/producers',
      reservation: (sku: string) => `admin/products/${sku}/reservations`,
      reservations: (sku: string, client_slug: string) =>
        `admin/products/${sku}/reservations/${client_slug}`,
      grapeTypes: '/admin/products/grape_types',
      discount: (sku: string) => `admin/products/${sku}/discounts`,
      deleteDiscounts: (id: string, product_id: string) =>
        `admin/products/${product_id}/discounts/${id}`,
    },
    producers: '/admin/producers',
    users: '/admin/users',
    statistics: {
      root: '/admin/stats',
      saleStats: '/admin/sale-stats',
      bestSellingProductsStats: '/admin/best-selling-sale-stats',
      annualStats: {
        yoy: '/admin/stats/yoy',
      },
      annualStat: '/admin/stats/annual-stat',
      wineType: '/admin/stats/wine-type',
      getYoyStats: '/admin/stats/get-yoy-stat',
      getOrderPlacedByStats: '/admin/stats/order-stats',
      productStats: '/admin/product_stats',
      updateLogisticsCost: '/admin/stats/update_logistics_cost',
      profitability: 'admin/stats/profitability',
    },
    reservations: '/admin/reservations',
    tastings: '/admin/tastings',
  },
  customer: {
    producers: { root: '/customer/producers', countries: '/customer/producers/countries' },
    products: {
      root: '/customer/products',
      volumes: '/customer/products/volumes',
      grape_types: '/customer/products/grape_types',
      wine_types: '/customer/products/wine_types',
    },
    client_orders: {
      root: '/customer/client_orders',
      current: '/customer/client_orders/current',
      current_order: '/customer/client_orders/currentOrder',
    },
    cart_products: {
      root: '/customer/cart_products',
    },
  },
};

// ----------------------------------------------------------------------

const createAxiosInstance = ({
  baseURL,
  isLegacyURL = false,
}: {
  baseURL: string;
  isLegacyURL?: boolean;
}) => {
  const tokenKey = isLegacyURL
    ? AUTH_STORAGE_KEYS.accessTokenLegacy
    : AUTH_STORAGE_KEYS.accessToken;

  // const refreshTokenKey = isLegacyURL
  //   ? AUTH_STORAGE_KEYS.refreshTokenLegacy
  //   : AUTH_STORAGE_KEYS.refreshToken;
  // const refreshToken = localStorageGetItem(refreshTokenKey);

  // const refreshTokenPath = isLegacyURL
  //   ? endpointsLegacy.auth.refreshToken
  //   : endpoints.auth.refreshToken;

  const customAxiosInstance = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  const firebaseApp = initializeApp(FIREBASE_API);

  const AUTH = getAuth(firebaseApp);

  customAxiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = localStorageGetItem(tokenKey);

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    (error) => Promise.reject(error),
  );

  const fetchCurrentUser = () =>
    new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        AUTH,
        (user: any) => {
          unsubscribe();
          resolve(user);
        },
        reject,
      );
    });

  const refreshAuthToken = async (failedRequest: any) => {
    try {
      const userData = JSON.parse(localStorageGetItem(AUTH_STORAGE_KEYS.user) || '{}');

      if (!userData) {
        return await Promise.reject(failedRequest);
      }

      // const { data: res } = await customAxiosInstance.post(`${baseURL}${refreshTokenPath}`, {
      //   refreshToken,
      // });

      const currentUser: any = await fetchCurrentUser();
      if (!currentUser) {
        throw new Error('User not authenticated');
      }
      if (currentUser) {
        const accessToken = await currentUser.getIdToken(true);
        localStorageSetItem(AUTH_STORAGE_KEYS.accessToken, accessToken);
        localStorageSetItem(AUTH_STORAGE_KEYS.refreshToken, currentUser.refreshToken);
      }

      return await Promise.resolve();
    } catch (error) {
      Object.keys(AUTH_STORAGE_KEYS).forEach((key) => {
        localStorageRemoveItem(key);
      });
      // window.location.href = `${window.location.protocol}//${window.location.hostname}${
      //   ENVIRONMENTS.dev === ENVIRONMENT ? `:${window.location.port}` : ''
      // }${paths.auth.login}`;

      window.location.href = `${paths.login}`;
      return Promise.reject(error);
    }
  };

  createAuthRefreshInterceptor(customAxiosInstance, refreshAuthToken, {
    statusCodes: [401, 403],
  });

  // customAxiosInstance.interceptors.response.use(
  //   (res) => res,
  //   (error) => Promise.reject((error.response && error.response.data) || 'Something went wrong'),
  //   // (error) => {
  //   //   SnackbarUtils.error(`${error}`);
  //   //   return Promise.reject((error.response && error.response.data) || 'Something went wrong');
  //   // },
  // );

  return customAxiosInstance;
};

export const axiosInstanceLegacy = createAxiosInstance({
  baseURL: HOST_API_LEGACY as string,
  isLegacyURL: false,
});
const axiosInstance = createAxiosInstance({ baseURL: HOST_API as string });
export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcherLegacy = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const { data } = await axiosInstanceLegacy.get(url, { ...config });

  return data;
};

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const {
    data: { data },
  } = await axiosInstance.get(url, { ...config });

  return data;
};
